import type { Colour } from '@risksmart-app/components/src/utils/colours';
import { colours } from '@risksmart-app/components/src/utils/colours';
import i18n from '@risksmart-app/i18n/src/i18n';
import type { GetEnterpriseRisksFlatQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  useGetEnterpriseRisksRegister,
  type UseGetEnterpriseRisksRegisterArgs,
} from 'src/hooks/queries/enterprise-risk/useGetEnterpriseRisksRegister';
import { useGetEnterpriseRiskSmartWidgetTableProps } from 'src/pages/enterprise-risk/config';
import type { EnterpriseRiskRegisterFields } from 'src/pages/enterprise-risk/types';

import { enterpriseRiskRegisterUrl } from '@/utils/urls';

import { UNRATED } from '../../gigawidget/types';
import { toIsoBounds } from '../../gigawidget/util/filterHelpers';
import { createDataSource } from '../createDataSource';
import {
  dashboardDateRangeClickthroughFilter,
  dateRangeClickthroughFilter,
  defaultClickthroughFilter,
} from '../dataSourceHelpers';

export default createDataSource<
  EnterpriseRiskRegisterFields,
  UseGetEnterpriseRisksRegisterArgs,
  GetEnterpriseRisksFlatQuery
>({
  group: 'single',
  hasAccess: (_, isModuleEnabled) => isModuleEnabled('enterprise_risk'),
  parentTypes: [Parent_Type_Enum.EnterpriseRisk],
  useWidgetQuery: useGetEnterpriseRisksRegister,
  useTablePropsHook: (data, options) => {
    return useGetEnterpriseRiskSmartWidgetTableProps(
      data?.enterprise_risk,
      options
    );
  },
  entityNamePlural: 'enterprise_risk_other',
  entityNameSingular: 'enterprise_risk_one',
  fields: 'enterpriseRisks.fields',
  dashboardFilterConfig: {
    tagsFilter: (_tags) => ({}),
    departmentsFilter: (_departments) => ({}),
    // Legacy stub, deliberately NOT filled by COI-878: this default row filter
    // has never narrowed on either leg, so filling it would be new capability
    // for the non-date categories (tier, treatment, residual rating) rather
    // than a restoration. The `createdDate` category's override is the one that
    // was lost in COI-600 and is restored above.
    dateFilter: (_dateRange, _precision) => ({}),
    dateClickthroughFilter:
      dashboardDateRangeClickthroughFilter('CreatedAtTimestamp'),
  },
  clickThroughUrl: (filter) => enterpriseRiskRegisterUrl(filter),
  categoryGetters: [
    // CUS-221: risk name as a grouping dimension, so an ERM chart can be
    // split by individual risk instead of only rolling up by rating. Title is
    // non-nullable on enterprise_risk, so there is no unrated bucket to
    // handle — unlike the parent-risk getter on the Risks data source
    // (RSP-5585), and unlike the obligation/consequence title getters, which
    // reach for defaultClickthroughFilterWithUnrated.
    {
      id: 'title',
      name: () => i18n.t('enterpriseRisks.fields.title'),
      categoryGetter: (item) => ({ key: item.Title, label: item.Title }),
      clickthroughFilter: defaultClickthroughFilter('Title'),
    },
    {
      id: 'tier',
      name: () => i18n.t('risks.columns.risk_tier'),
      categoryGetter: (item) => item.TierLabelled,
      clickthroughFilter: defaultClickthroughFilter('TierLabelled'),
    },
    {
      id: 'treatment',
      name: () => i18n.t('risks.columns.risk_treatment'),
      categoryGetter: (item) => item.TreatmentLabelled,
      clickthroughFilter: defaultClickthroughFilter('TreatmentLabelled'),
    },
    {
      id: 'residualRatingMean',
      name: () => i18n.t('enterpriseRisks.columns.residualRatingMean'),
      categoryGetter: (item) => ({
        key: item.ResidualMeanLabelled ?? UNRATED,
        label: item.ResidualMeanLabelled ?? UNRATED,
        sortKey: item.ResidualMeanLabelled ?? UNRATED,
      }),
      ratingColourKey: 'risk_controlled',
      clickthroughFilter: defaultClickthroughFilter('ResidualMeanLabelled', {
        unratedValue: UNRATED,
      }),
      categoryOverrideFunction: (category, _ratingFns, riskFormatters) => {
        const rating = riskFormatters({
          residualRating: category.data[0]?.score?.ResidualRatingMean,
        }).getResidualOption();

        return {
          title: rating?.label ?? UNRATED,
          color:
            colours[(rating?.color ?? 'light-grey') as Colour]
              ?.backgroundColor ?? rating?.color,
        };
      },
    },
    {
      id: 'residualRatingWorstCase',
      name: () => i18n.t('enterpriseRisks.columns.residualRatingWorstCase'),
      categoryGetter: (item) => ({
        key: item.ResidualWorstCaseLabelled ?? UNRATED,
        label: item.ResidualWorstCaseLabelled ?? UNRATED,
        sortKey: item.ResidualWorstCaseLabelled ?? UNRATED,
      }),
      ratingColourKey: 'risk_controlled',
      clickthroughFilter: defaultClickthroughFilter(
        'ResidualWorstCaseLabelled',
        {
          unratedValue: UNRATED,
        }
      ),
      categoryOverrideFunction: (category, _ratingFns, riskFormatters) => {
        const rating = riskFormatters({
          residualRating: category.data[0]?.score?.ResidualRatingWorstCase,
        }).getResidualOption();

        return {
          title: rating?.label ?? UNRATED,
          color:
            colours[(rating?.color ?? 'light-grey') as Colour]
              ?.backgroundColor ?? rating?.color,
        };
      },
    },
    {
      id: 'inherentRatingMean',
      name: () => i18n.t('enterpriseRisks.columns.inherentRatingMean'),
      categoryGetter: (item) => ({
        key: item.InherentMeanLabelled ?? UNRATED,
        label: item.InherentMeanLabelled ?? UNRATED,
        sortKey: item.InherentMeanLabelled ?? UNRATED,
      }),
      ratingColourKey: 'risk_uncontrolled',
      clickthroughFilter: defaultClickthroughFilter('InherentMeanLabelled', {
        unratedValue: UNRATED,
      }),
      categoryOverrideFunction: (category, _ratingFns, riskFormatters) => {
        const rating = riskFormatters({
          inherentRating: category.data[0]?.score?.InherentRatingMean,
        }).getInherentOption();

        return {
          title: rating?.label ?? UNRATED,
          color:
            colours[(rating?.color ?? 'light-grey') as Colour]
              ?.backgroundColor ?? rating?.color,
        };
      },
    },
    {
      id: 'inherentRatingWorstCase',
      name: () => i18n.t('enterpriseRisks.columns.inherentRatingWorstCase'),
      categoryGetter: (item) => ({
        key: item.InherentWorstCaseLabelled ?? UNRATED,
        label: item.InherentWorstCaseLabelled ?? UNRATED,
        sortKey: item.InherentWorstCaseLabelled ?? UNRATED,
      }),
      ratingColourKey: 'risk_uncontrolled',
      clickthroughFilter: defaultClickthroughFilter(
        'InherentWorstCaseLabelled',
        {
          unratedValue: UNRATED,
        }
      ),
      categoryOverrideFunction: (category, _ratingFns, riskFormatters) => {
        const rating = riskFormatters({
          inherentRating: category.data[0]?.score?.InherentRatingWorstCase,
        }).getInherentOption();

        return {
          title: rating?.label ?? UNRATED,
          color:
            colours[(rating?.color ?? 'light-grey') as Colour]
              ?.backgroundColor ?? rating?.color,
        };
      },
    },
    // Aggregated likelihood/impact dimensions (RSP-6195). Labelled values
    // resolve through the scoring settings (taxonomy fallback); fractional
    // medians carry the bare number as their label.
    {
      id: 'inherentLikelihoodMean',
      name: () => i18n.t('enterpriseRisks.columns.inherentLikelihoodMean'),
      categoryGetter: (item) => item.InherentLikelihoodMeanLabelled ?? UNRATED,
      ratingColourKey: 'likelihood',
      clickthroughFilter: defaultClickthroughFilter(
        'InherentLikelihoodMeanLabelled',
        { unratedValue: UNRATED }
      ),
    },
    {
      id: 'inherentImpactMean',
      name: () => i18n.t('enterpriseRisks.columns.inherentImpactMean'),
      categoryGetter: (item) => item.InherentImpactMeanLabelled ?? UNRATED,
      ratingColourKey: 'impact',
      clickthroughFilter: defaultClickthroughFilter(
        'InherentImpactMeanLabelled',
        { unratedValue: UNRATED }
      ),
    },
    {
      id: 'residualLikelihoodMean',
      name: () => i18n.t('enterpriseRisks.columns.residualLikelihoodMean'),
      categoryGetter: (item) => item.ResidualLikelihoodMeanLabelled ?? UNRATED,
      ratingColourKey: 'likelihood',
      clickthroughFilter: defaultClickthroughFilter(
        'ResidualLikelihoodMeanLabelled',
        { unratedValue: UNRATED }
      ),
    },
    {
      id: 'residualImpactMean',
      name: () => i18n.t('enterpriseRisks.columns.residualImpactMean'),
      categoryGetter: (item) => item.ResidualImpactMeanLabelled ?? UNRATED,
      ratingColourKey: 'impact',
      clickthroughFilter: defaultClickthroughFilter(
        'ResidualImpactMeanLabelled',
        { unratedValue: UNRATED }
      ),
    },
    {
      id: 'inherentLikelihoodMedian',
      name: () => i18n.t('enterpriseRisks.columns.inherentLikelihoodMedian'),
      categoryGetter: (item) =>
        item.InherentLikelihoodMedianLabelled ?? UNRATED,
      ratingColourKey: 'likelihood',
      clickthroughFilter: defaultClickthroughFilter(
        'InherentLikelihoodMedianLabelled',
        { unratedValue: UNRATED }
      ),
    },
    {
      id: 'inherentImpactMedian',
      name: () => i18n.t('enterpriseRisks.columns.inherentImpactMedian'),
      categoryGetter: (item) => item.InherentImpactMedianLabelled ?? UNRATED,
      ratingColourKey: 'impact',
      clickthroughFilter: defaultClickthroughFilter(
        'InherentImpactMedianLabelled',
        { unratedValue: UNRATED }
      ),
    },
    {
      id: 'residualLikelihoodMedian',
      name: () => i18n.t('enterpriseRisks.columns.residualLikelihoodMedian'),
      categoryGetter: (item) =>
        item.ResidualLikelihoodMedianLabelled ?? UNRATED,
      ratingColourKey: 'likelihood',
      clickthroughFilter: defaultClickthroughFilter(
        'ResidualLikelihoodMedianLabelled',
        { unratedValue: UNRATED }
      ),
    },
    {
      id: 'residualImpactMedian',
      name: () => i18n.t('enterpriseRisks.columns.residualImpactMedian'),
      categoryGetter: (item) => item.ResidualImpactMedianLabelled ?? UNRATED,
      ratingColourKey: 'impact',
      clickthroughFilter: defaultClickthroughFilter(
        'ResidualImpactMedianLabelled',
        { unratedValue: UNRATED }
      ),
    },
    {
      id: 'inherentLikelihoodWorstCase',
      name: () => i18n.t('enterpriseRisks.columns.inherentLikelihoodWorstCase'),
      categoryGetter: (item) =>
        item.InherentLikelihoodWorstCaseLabelled ?? UNRATED,
      ratingColourKey: 'likelihood',
      clickthroughFilter: defaultClickthroughFilter(
        'InherentLikelihoodWorstCaseLabelled',
        { unratedValue: UNRATED }
      ),
    },
    {
      id: 'inherentImpactWorstCase',
      name: () => i18n.t('enterpriseRisks.columns.inherentImpactWorstCase'),
      categoryGetter: (item) => item.InherentImpactWorstCaseLabelled ?? UNRATED,
      ratingColourKey: 'impact',
      clickthroughFilter: defaultClickthroughFilter(
        'InherentImpactWorstCaseLabelled',
        { unratedValue: UNRATED }
      ),
    },
    {
      id: 'residualLikelihoodWorstCase',
      name: () => i18n.t('enterpriseRisks.columns.residualLikelihoodWorstCase'),
      categoryGetter: (item) =>
        item.ResidualLikelihoodWorstCaseLabelled ?? UNRATED,
      ratingColourKey: 'likelihood',
      clickthroughFilter: defaultClickthroughFilter(
        'ResidualLikelihoodWorstCaseLabelled',
        { unratedValue: UNRATED }
      ),
    },
    {
      id: 'residualImpactWorstCase',
      name: () => i18n.t('enterpriseRisks.columns.residualImpactWorstCase'),
      categoryGetter: (item) => item.ResidualImpactWorstCaseLabelled ?? UNRATED,
      ratingColourKey: 'impact',
      clickthroughFilter: defaultClickthroughFilter(
        'ResidualImpactWorstCaseLabelled',
        { unratedValue: UNRATED }
      ),
    },
    {
      id: 'createdDate',
      name: () => i18n.t('columns.created_on'),
      categoryGetter: (data) => new Date(data.CreatedAtTimestamp),
      date: true,
      dashboardDateFilterOverride: (dateRange, precision) => ({
        dateRange: {
          field: 'CreatedAtTimestamp',
          ...toIsoBounds(dateRange, precision),
        },
      }),
      clickthroughFilter: dateRangeClickthroughFilter('CreatedAtTimestamp'),
    },
  ],
});
